import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { Link } from 'react-router-dom';

const SERVICES_SCHEMA = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": `${import.meta.env.VITE_SITE_URL}/services#webpage`,
      "url": `${import.meta.env.VITE_SITE_URL}/services`,
      "name": "Pressure Washing Services Dallas TX | DVS Power Washing",
      "description": "Professional pressure washing services in Dallas Metroplex, TX: house washing, driveway cleaning, roof soft washing, fence & patio cleaning, and commercial pressure washing.",
      "isPartOf": { "@id": `${import.meta.env.VITE_SITE_URL}/#website` },
      "breadcrumb": {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": import.meta.env.VITE_SITE_URL },
          { "@type": "ListItem", "position": 2, "name": "Services", "item": `${import.meta.env.VITE_SITE_URL}/services` }
        ]
      }
    },
    {
      "@type": "ItemList",
      "name": "DVS Power Washing Services",
      "itemListElement": [
        {
          "@type": "ListItem", "position": 1,
          "item": {
            "@type": "Service",
            "name": "House Washing",
            "description": "Safe exterior cleaning to remove dirt, mold, mildew, and buildup from your home's siding, stucco, brick, and other surfaces.",
            "provider": { "@id": `${import.meta.env.VITE_SITE_URL}/#business` },
            "areaServed": "Dallas Metroplex, TX"
          }
        },
        {
          "@type": "ListItem", "position": 2,
          "item": {
            "@type": "Service",
            "name": "Driveway & Sidewalk Cleaning",
            "description": "Restore your driveway, sidewalks, and walkways to like-new condition by eliminating oil stains, tire marks, dirt, and grime.",
            "provider": { "@id": `${import.meta.env.VITE_SITE_URL}/#business` },
            "areaServed": "Dallas Metroplex, TX"
          }
        },
        {
          "@type": "ListItem", "position": 3,
          "item": {
            "@type": "Service",
            "name": "Roof Soft Washing",
            "description": "Specialized low-pressure soft wash system safely removes algae, moss, lichen, and black streaks from your roof.",
            "provider": { "@id": `${import.meta.env.VITE_SITE_URL}/#business` },
            "areaServed": "Dallas Metroplex, TX"
          }
        },
        {
          "@type": "ListItem", "position": 4,
          "item": {
            "@type": "Service",
            "name": "Fence & Patio Cleaning",
            "description": "Clean wood, vinyl, and composite fences, as well as patios, decks, and pool areas.",
            "provider": { "@id": `${import.meta.env.VITE_SITE_URL}/#business` },
            "areaServed": "Dallas Metroplex, TX"
          }
        },
        {
          "@type": "ListItem", "position": 5,
          "item": {
            "@type": "Service",
            "name": "Commercial Pressure Washing",
            "description": "Professional cleaning for businesses, storefronts, office buildings, parking lots, and commercial properties.",
            "provider": { "@id": `${import.meta.env.VITE_SITE_URL}/#business` },
            "areaServed": "Dallas Metroplex, TX"
          }
        }
      ]
    },
    {
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What pressure washing services does DVS Power Washing offer in Dallas TX?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "DVS Power Washing offers house washing, driveway and sidewalk cleaning, roof soft washing, fence and patio cleaning, and commercial pressure washing throughout the Dallas Metroplex, TX."
          }
        },
        {
          "@type": "Question",
          "name": "Is soft washing safe for my roof?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. Our roof soft washing uses a low-pressure system specifically designed to safely remove algae, moss, lichen, and black streaks without damaging shingles or roofing materials."
          }
        },
        {
          "@type": "Question",
          "name": "Do you offer free estimates for pressure washing in Dallas?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Absolutely. DVS Power Washing provides free, no-obligation estimates for all pressure washing services in the Dallas Metroplex. Call (214) 636-8343 or fill out our online form."
          }
        },
        {
          "@type": "Question",
          "name": "What areas do you serve in the Dallas Metroplex?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "We serve Dallas, Fort Worth, Plano, Irving, Frisco, McKinney, Arlington, Garland, Richardson, Grand Prairie, Carrollton, Denton, and surrounding areas."
          }
        },
        {
          "@type": "Question",
          "name": "Can you pressure wash commercial properties in Dallas?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Yes. We provide professional commercial pressure washing for businesses, storefronts, office buildings, and parking lots throughout the Dallas Metroplex with flexible scheduling."
          }
        }
      ]
    }
  ]
};

const ServicesPage = () => {
  const { hash } = useLocation();

  useEffect(() => {
    // Canonical tag
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.rel = 'canonical';
      document.head.appendChild(canonical);
    }
    canonical.href = 'https://dvspowerwashingdallas.com/services';

    // Schema
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'schema-services';
    script.text = JSON.stringify(SERVICES_SCHEMA);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById('schema-services');
      if (el) el.remove();
    };
  }, []);

  useEffect(() => {
    if (hash) {
      const id = hash.replace('#', '');
      const el = document.getElementById(id);
      if (el) {
        setTimeout(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [hash]);

  const services = [
    {
      id: 'house-washing',
      title: 'House Washing/Exterior cleaning',
      description: 'Safe and effective exterior cleaning to remove dirt, mold, mildew, and buildup from your home\'s siding, stucco, brick, and other surfaces. Our soft wash system protects your property while delivering exceptional results.',
      benefits: [
        'Removes dirt, mold, and mildew safely',
        'Protects paint and siding materials',
        'Enhances curb appeal instantly',
        'Extends the life of your exterior',
      ],
      image: 'https://static.readdy.ai/image/cd53c194d2b27431271da8b5defffc63/18ca562e2caea14a44f49d0bdbec7203.jpeg',
    },
    {
      id: 'driveway-cleaning',
      title: 'Driveway & Sidewalk Cleaning',
      description: 'Restore your driveway, sidewalks, and walkways to like-new condition. We eliminate oil stains, tire marks, dirt, and grime that accumulate over time, dramatically improving your property\'s curb appeal.',
      benefits: [
        'Removes tough oil and tire stains',
        'Eliminates slippery algae and mold',
        'Restores original concrete color',
        'Increases property value',
      ],
      image: 'https://static.readdy.ai/image/cd53c194d2b27431271da8b5defffc63/815b0660865f0e79f95ec70017ed5e51.jpeg',
    },
    {
      id: 'roof-washing',
      title: 'Roof Soft Washing',
      description: 'Our specialized low-pressure soft wash system safely removes algae, moss, lichen, and black streaks from your roof without damaging shingles. Extend your roof\'s lifespan and improve your home\'s appearance.',
      benefits: [
        'Safe for all roofing materials',
        'Removes algae and black streaks',
        'Extends roof lifespan',
        'Improves energy efficiency',
      ],
      image: 'https://public.readdy.ai/ai/img_res/0a98d9e3e7c70f721cd6f366159e9744.jpg',
    },
    {
      id: 'fence-patio',
      title: 'Fence & Patio Cleaning',
      description: 'Bring your outdoor living spaces back to life. We clean wood, vinyl, and composite fences, as well as patios, decks, and pool areas, removing years of dirt, mildew, and weathering.',
      benefits: [
        'Restores wood and vinyl fences',
        'Cleans patios and pool decks',
        'Removes mold and mildew',
        'Prepares surfaces for staining',
      ],
      image: 'https://static.readdy.ai/image/cd53c194d2b27431271da8b5defffc63/72c587a51d8ef326e02063cc7a076820.jpeg',
    },
    {
      id: 'commercial',
      title: 'Commercial Pressure Washing',
      description: 'Professional cleaning services for businesses, storefronts, office buildings, parking lots, and commercial properties. Maintain a clean, professional appearance that attracts customers and clients.',
      benefits: [
        'Enhances business curb appeal',
        'Maintains professional appearance',
        'Flexible scheduling available',
        'Large-scale project capability',
      ],
      image: 'https://readdy.ai/api/search-image?query=clean-commercial-building-storefront-and-parking-lot-after-professional-pressure-washing-modern-business-exterior-in-florida-bright-pristine-surfaces-professional-commercial-cleaning-results&width=800&height=600&seq=service-commercial-005&orientation=landscape',
    },
  ];

  return (
    <div className="min-h-screen">
      <Navbar />

      <section className="relative h-[400px] flex items-center justify-center">
        <div className="absolute inset-0 bg-gradient-to-r from-[#0E3A5D] to-[#1A5280]">
          <div className="absolute inset-0 opacity-30">
            <img
              src="https://readdy.ai/api/search-image?query=professional-pressure-washing-equipment-and-tools-commercial-grade-machinery-water-spray-action-shot-florida-outdoor-setting-high-quality-cleaning-service-background&width=1920&height=800&seq=services-hero-bg-001&orientation=landscape"
              alt="Professional Services"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h1 className="text-4xl lg:text-6xl font-bold text-white mb-6">Our Services</h1>
          <p className="text-xl lg:text-2xl text-[#4FB3D9]">
            Professional exterior cleaning solutions
          </p>
        </div>
      </section>

      {services.map((service, index) => (
        <section
          key={index}
          id={service.id}
          className={`py-20 ${index % 2 === 0 ? 'bg-white' : 'bg-[#F5F7FA]'}`}
        >
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:flex-row-reverse' : ''}`}>
              <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                <img
                  src={service.image}
                  alt={service.title}
                  className="w-full h-[400px] object-cover object-center rounded-2xl shadow-xl"
                />
              </div>

              <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                <h2 className="text-3xl lg:text-4xl font-bold text-[#0E3A5D] mb-6">
                  {service.title}
                </h2>
                <p className="text-lg text-[#5A6C7D] leading-relaxed mb-6">
                  {service.description}
                </p>

                <ul className="space-y-3 mb-8">
                  {service.benefits.map((benefit, idx) => (
                    <li key={idx} className="flex items-start space-x-3">
                      <i className="ri-checkbox-circle-fill text-[#4FB3D9] text-xl mt-1"></i>
                      <span className="text-base text-gray-700">{benefit}</span>
                    </li>
                  ))}
                </ul>

                <Link
                  to="/contact"
                  className="inline-block bg-[#0E3A5D] text-white px-8 py-4 rounded-lg font-bold hover:bg-[#0A2D47] transition-all whitespace-nowrap"
                >
                  Request Quote
                </Link>
              </div>
            </div>
          </div>
        </section>
      ))}

      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-[#0E3A5D] mb-6">
            Ready to Transform Your Property?
          </h2>
          <p className="text-xl text-[#5A6C7D] mb-10">
            Call today for a free estimate
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <a
              href="tel:2146368343"
              className="bg-[#4FB3D9] text-white px-10 py-4 rounded-lg text-lg font-bold hover:bg-[#3A9DC4] transition-all whitespace-nowrap flex items-center space-x-3 w-full sm:w-auto justify-center"
            >
              <i className="ri-phone-fill text-xl"></i>
              <span>Call (214) 636-8343</span>
            </a>
            <Link
              to="/contact"
              className="bg-[#0E3A5D] text-white px-10 py-4 rounded-lg text-lg font-bold hover:bg-[#0A2D47] transition-all whitespace-nowrap w-full sm:w-auto text-center"
            >
              Request Free Quote
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ServicesPage;
